import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSelector } from "react-redux";
import Classes from "../AddAppointment.module.css";

const Overlay = ({ children, onClose }) => (
  <div
    role="dialog"
    aria-modal="true"
    onClick={(e) => {
      if (e.currentTarget === e.target && onClose) onClose();
    }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.4)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      zIndex: 1000,
      overflowY: "auto",
    }}
  >
    {children}
  </div>
);

const Card = ({ children }) => (
  <div
    style={{
      width: "clamp(320px, 92vw, 720px)",
      background: "#fff",
      borderRadius: 12,
      padding: "clamp(16px, 2vw, 24px)",
      boxShadow: "0 20px 60px rgba(0,0,0,.20)",
      maxHeight: "86vh",
      overflow: "auto",
      margin: "auto",
    }}
  >
    {children}
  </div>
);

/* Small helpers for local total calc */
const PRICE_KEY = { reg: "regular", mid: "mid", prem: "premium" };
const toBool = (v) => v === true || v === 1 || v === "1";
const parseGallons = (val) => {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : 0;
};

function isSubscriptionActive(as) {
  if (!as) return false;
  const status = (as.status || 'none').toLowerCase();
  if (status === 'none' || status === 'canceled' || status === 'cancelled') return false;
  const now = Date.now();
  const startOk = as.current_period_start ? new Date(as.current_period_start).getTime() <= now : true;
  const endOk   = as.current_period_end   ? new Date(as.current_period_end).getTime()   >= now : false;
  return startOk && endOk;
}

function within24Hours(createdAt) {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() - t <= 24 * 60 * 60 * 1000;
}

/* Simple custom toggle (no MUI) */
function EmergencyToggle({ value, onChange, label = "Add Emergency Delivery", amount }) {
  const isOn = !!value;
  const toggle = () => onChange(!isOn);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div
      className={Classes.textAreaCon}
      style={{
        marginTop: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button
          type="button"
          role="switch"
          aria-checked={isOn}
          onClick={toggle}
          onKeyDown={onKey}
          style={{
            width: 46,
            height: 26,
            borderRadius: 999,
            border: "1px solid #e5e7eb",
            background: isOn ? "var(--secondary-color)" : "#f3f4f6",
            display: "inline-flex",
            alignItems: "center",
            padding: 2,
            transition: "background .15s ease",
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 1px 3px rgba(0,0,0,.2)",
              transform: `translateX(${isOn ? 20 : 0}px)`,
              transition: "transform .15s ease",
              display: "block",
            }}
          />
        </button>
        <h5 className={Classes.label} style={{ margin: 0 }}>{label}</h5>
      </div>
      <div className={Classes.text} style={{ minWidth: 120, textAlign: "right" }}>
        {isOn ? `+$${Number(amount || 0).toFixed(2)}` : "+$0.00"}
      </div>
    </div>
  );
}

export default function ConfirmBookingModal({
  open,
  onClose,
  draft,
  service,
  rush,          // initial value only
  setRush,       // optional: keep parent in sync
  onConfirm,     // (currentRush: boolean) => void
}) {
  const { user } = useSelector((s) => s.auth);
  const activeSub = user?.activeSubscription || null;

  const isSubbed = isSubscriptionActive(activeSub);
  const isNewUserOffer = !isSubbed && within24Hours(user?.createdAt);

  const [localRush, setLocalRush] = useState(false);
  useEffect(() => {
    if (open) setLocalRush(toBool(rush));
  }, [open, rush]);

  // Compute totals
  const totals = useMemo(() => {
    const priceFor = (fuelTypeId) => {
      const key = PRICE_KEY[fuelTypeId] || "regular";
      return Number(service?.prices?.[key] || 0);
    };

    let subtotal = parseGallons(draft?.vehicle?.requestedAmount) * priceFor(draft?.vehicle?.fuelTypeId);
    for (const ev of draft?.extraVehicles || []) {
      subtotal += parseGallons(ev?.requestedAmount) * priceFor(ev?.fuelTypeId);
    }

    // ✅ Delivery fee behavior:
    // - membership active => $0
    // - new signup within 24h => $0
    // - else use configured fee
    const configuredDelivery = Number(service?.fees?.delivery ?? service?.prices?.deliveryFee ?? 9.99);
    const deliveryFee = (isSubbed || isNewUserOffer) ? 0 : configuredDelivery;

    const baseEmergency = Number(service?.fees?.emergency ?? service?.prices?.emergencyFee ?? 0);
    const emergencyFee = localRush ? baseEmergency : 0;

    const total = Number(subtotal + deliveryFee + emergencyFee);

    return {
      subtotal: Number(subtotal.toFixed(2)),
      deliveryFee: Number(deliveryFee.toFixed(2)),
      emergencyFee: Number(emergencyFee.toFixed(2)),
      total: Number(total.toFixed(2)),
      baseEmergency,
    };
  }, [draft, service, localRush, isSubbed, isNewUserOffer]);

  // close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const { subtotal, deliveryFee, emergencyFee, total, baseEmergency } = totals;

  const handleToggle = (next) => {
    setLocalRush(next);
    if (typeof setRush === "function") setRush(!!next);
  };

  const handleConfirm = () => onConfirm?.(!!localRush);

  return (
    <AnimatePresence>
      <Overlay onClose={onClose}>
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          style={{ width: "100%" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Card>
            <div className={Classes.flexBetween} style={{ marginBottom: 6 }}>
              <h3 className={Classes.title3} style={{ margin: 0 }}>Confirm Your Booking</h3>
              <button className={Classes.linkBtn} onClick={onClose} type="button">Close</button>
            </div>

            {/* ✅ Updated note (no “$9.99 applies” line anymore) */}
            <h5 className={Classes.text} style={{ marginTop: 2 }}>
              {isSubbed
                ? "Membership active — delivery benefits applied."
                : isNewUserOffer
                ? "New user offer — free delivery for your first 24 hours."
                : "Review your booking details before confirming."}
            </h5>

            {/* Date/Time */}
            <div className={Classes.textAreaCon} style={{ marginTop: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <h5 className={Classes.label}>Date</h5>
                  <div className={Classes.inputField} style={{ display: "flex", alignItems: "center" }}>
                    {draft?.appointment?.date || "-"}
                  </div>
                </div>
                <div>
                  <h5 className={Classes.label}>Time Slot</h5>
                  <div className={Classes.inputField} style={{ display: "flex", alignItems: "center" }}>
                    {draft?.appointment?.timeSlot || "-"}
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicles */}
            <div className={Classes.textAreaCon} style={{ marginTop: 12 }}>
              <h5 className={Classes.label} style={{ marginBottom: 6 }}>Vehicles</h5>
              {[{ ...draft.vehicle, __idx: 0 }, ...(draft.extraVehicles || []).map((v, i) => ({ ...v, __idx: i + 1 }))].map(
                (v) => (
                  <div
                    key={v.__idx}
                    className={Classes.flexBetween}
                    style={{ padding: "6px 0", borderBottom: "1px dashed #eee" }}
                  >
                    <div><strong>{v.make || "-"}</strong> {v.model || ""} {v.license ? `• ${v.license}` : ""}</div>
                    <div>{v.fuelTypeId || "-"} • {v.requestedAmount || 0} gal</div>
                  </div>
                )
              )}
            </div>

            {/* Location */}
            <div className={Classes.textAreaCon} style={{ marginTop: 12 }}>
              <h5 className={Classes.label} style={{ marginBottom: 6 }}>Location</h5>
              <div>
                {(draft?.location?.address1 || "-")},{" "}
                {draft?.location?.city || "-"},{" "}
                {draft?.location?.region || "-"} {draft?.location?.postalCode || ""} {draft?.location?.country || ""}
              </div>
              {(draft?.location?.parkingSpotOrNotes || draft?.location?.gateCode) && (
                <div className={Classes.text} style={{ marginTop: 6 }}>
                  {draft?.location?.parkingSpotOrNotes ? `Notes: ${draft.location.parkingSpotOrNotes}` : ""}
                  {draft?.location?.gateCode ? `  • Gate: ${draft.location.gateCode}` : ""}
                </div>
              )}
            </div>

            {/* Optional emergency toggle */}
            {/* <EmergencyToggle value={localRush} onChange={handleToggle} amount={baseEmergency} /> */}

            {/* Totals */}
            <div className={Classes.textAreaCon} style={{ marginTop: 12 }}>
              <div className={Classes.flexBetween} style={{ padding: "6px 0" }}>
                <div>Fuel Subtotal</div><strong>${subtotal.toFixed(2)}</strong>
              </div>

              {/* ✅ Delivery fee row removed from UI when you asked “remove deliveryfee from here”.
                  If you still want to show it when non-zero, change condition to: deliveryFee > 0 */}
              {deliveryFee > 0 ? null : (
                <div className={Classes.flexBetween} style={{ padding: "6px 0" }}>
                  <div>Delivery Fee</div><strong>$0.00</strong>
                </div>
              )}

              <div className={Classes.flexBetween} style={{ padding: "6px 0", borderBottom: "1px dashed #eee" }}>
                <div>Emergency Fee</div><strong>${Number(emergencyFee).toFixed(2)}</strong>
              </div>

              <div className={Classes.flexBetween} style={{ padding: "10px 0" }}>
                <h4 style={{ margin: 0 }}>Total</h4><h4 style={{ margin: 0 }}>${total.toFixed(2)}</h4>
              </div>
            </div>

            {/* Actions */}
            <div className={Classes.navRow}>
              <button type="button" className={Classes.backBtn} onClick={onClose}><h5>Back</h5></button>
              <button type="button" className={Classes.saveBtn} onClick={handleConfirm}><h5>Confirm &amp; Pay</h5></button>
            </div>
          </Card>
        </motion.div>
      </Overlay>
    </AnimatePresence>
  );
}
